import React, { useState } from "react";
import { api } from "../../lib/api";
import {
  AlertTriangle, Clock, MapPin, User, Calendar,
  CheckCircle, Star, Car, Award, Loader2, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

export default function CoverageEventPanel({ events, onAssign }) {
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const handleAssign = async (eventId, caregiverId) => {
    setAssigning(`${eventId}-${caregiverId}`);
    try {
      await api.post(`/scheduling/coverage-events/${eventId}/assign`, {
        replacement_caregiver_id: caregiverId,
      });
      onAssign();
    } catch (err) {
      console.error("Failed to assign:", err);
      alert(err.response?.data?.detail || "Failed to assign caregiver");
    } finally {
      setAssigning(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-600 bg-emerald-50";
    if (score >= 60) return "text-blue-600 bg-blue-50";
    if (score >= 40) return "text-amber-600 bg-amber-50";
    return "text-gray-600 bg-gray-50";
  };

  if (events.length === 0) {
    return (
      <div className="py-12 text-center">
        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h3 className="font-medium text-gray-900">All shifts covered!</h3>
        <p className="text-sm text-gray-500 mt-1">
          No shifts currently need coverage.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      {events.map((event) => (
        <div
          key={event.id}
          className="border border-amber-200 rounded-xl overflow-hidden bg-white"
        >
          {/* Event Header */}
          <button
            onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
            className="w-full px-4 py-3 flex items-center justify-between bg-amber-50 hover:bg-amber-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div className="text-left">
                <div className="font-medium text-gray-900">
                  {event.client_name || "Client"}
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  {new Date(event.visit_date).toLocaleDateString()}
                  <Clock className="w-3 h-3 ml-2" />
                  {event.start_time} - {event.end_time}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                {event.event_type.replace("_", " ")}
              </Badge>
              {expandedEvent === event.id ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </button>

          {/* Expanded Content */}
          {expandedEvent === event.id && (
            <div className="p-4 border-t border-amber-100">
              {/* Reason */}
              {event.reason && (
                <div className="mb-4 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  <strong>Reason:</strong> {event.reason}
                </div>
              )}

              {/* Recommendations */}
              <div className="mb-3">
                <h4 className="font-medium text-gray-900 mb-2">
                  Replacement Recommendations
                </h4>
              </div>

              {!event.replacement_recommendations?.length ? (
                <p className="text-sm text-gray-500">
                  No available caregivers found. Try expanding your search criteria.
                </p>
              ) : (
                <div className="space-y-2">
                  {event.replacement_recommendations.slice(0, 5).map((rec) => (
                    <div
                      key={rec.caregiver_id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <div className="font-medium">{rec.name}</div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {rec.continuity_info?.previous_visits > 0 && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-purple-500" />
                                {rec.continuity_info.previous_visits} visits
                              </span>
                            )}
                            {rec.travel_info?.distance_miles && (
                              <span className="flex items-center gap-1">
                                <Car className="w-3 h-3 text-blue-500" />
                                {rec.travel_info.distance_miles} mi
                              </span>
                            )}
                          </div>
                          {rec.warnings?.length > 0 && (
                            <div className="text-xs text-amber-600 mt-1">
                              ⚠️ {rec.warnings[0]}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className={`px-2 py-1 rounded-full text-sm font-semibold ${getScoreColor(rec.match_score)}`}>
                          {rec.match_score}%
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAssign(event.id, rec.caregiver_id)}
                          disabled={assigning === `${event.id}-${rec.caregiver_id}`}
                          className="bg-[#06372B] hover:bg-[#0a4a3a]"
                        >
                          {assigning === `${event.id}-${rec.caregiver_id}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Assign"
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
